import React, { forwardRef } from 'react'
import AutocompleteBase, { useAutocompleteQuery } from 'transfix-ui/components/AutocompleteBase'
import {
  TAutocompleteBaseChangeHandler,
  IAutocompleteBaseOption,
} from 'transfix-ui/components/AutocompleteBase/types'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'
import { ITaskNamespaces, ITaskedShipmentsCount } from 'types/graphqlTypes'
import { TASK_NAMESPACE_AVAILABILITY_MAP } from '../../constants'
import { ITaskCapsQuery, useTaskCapsQuery } from '../../graphql/TaskCaps'
import { useTaskAssignmentRepsQuery } from './graphql/TaskAssignmentReps'

export type TRepOption = {
  id: string
  text: string
}

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: string | null
): IAutocompleteBaseOption | null => {
  const option = options.find(({ id }) => {
    return id === value
  })
  return option || null
}

export interface IAutocompleteRepsProps {
  value: string | null
  onChange: (newVal: IAutocompleteBaseOption | null) => void
  taskNamespace: ITaskNamespaces
}

export const getTaskQuantity = (
  taskCapMap: TTaskCapMap,
  assignedCount: ITaskedShipmentsCount[],
  taskNamespace: ITaskNamespaces
) => {
  const cap = taskCapMap[taskNamespace]
  const { count } = assignedCount.find(taskCount => taskCount.taskNamespace === taskNamespace) || {}
  return `${count}/${cap}`
}

type TTaskCapMap = Record<ITaskNamespaces, number>
const getTaskCapMap = (
  shipmentTaskCaps: ITaskCapsQuery['shipmentTaskCaps'] | undefined
): TTaskCapMap => {
  if (!shipmentTaskCaps) return {} as TTaskCapMap
  return shipmentTaskCaps.reduce((map, taskCap) => {
    const { cap, taskNamespace } = taskCap || {}
    return { ...map, [taskNamespace]: cap }
  }, {} as TTaskCapMap)
}

export const AutocompleteReps = forwardRef<HTMLInputElement, IAutocompleteRepsProps>(
  ({ onChange, taskNamespace, value: valueProp, ...props }, ref) => {
    const { data } = useTaskCapsQuery()
    const { shipmentTaskCaps } = data || {}
    const taskCapMap = getTaskCapMap(shipmentTaskCaps)

    const { options = [], loading } = useAutocompleteQuery(
      useTaskAssignmentRepsQuery,
      {
        variables: {
          type: TASK_NAMESPACE_AVAILABILITY_MAP[taskNamespace],
        },
        fetchPolicy: 'network-only', // keep availability and task counts up to date
      },
      {
        getOptionsConfig: data => {
          const { latestUserAvailabilitiesByType } = data || {}
          const { nodes } = latestUserAvailabilitiesByType || {}
          return {
            options: nodes || [],
            groupIndex: 1,
            mapOption: userAvailability => {
              const { isAvailable, user, userId } = userAvailability || {}
              const { assignedTasksCounts, fullName } = user || {}
              const taskAssignmentRatio = getTaskQuantity(
                taskCapMap,
                assignedTasksCounts || [],
                taskNamespace
              )
              return {
                id: userId,
                text: `${fullName} (${isAvailable ? taskAssignmentRatio : 'Unavailable'})`,
                disabled: !isAvailable,
              }
            },
          }
        },
      }
    )

    const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
      if (isAutocompleteBaseOption(newVal)) {
        onChange(newVal)
      } else {
        onChange(null)
      }
    }

    const value = getComputedValue(options, valueProp)

    return (
      <AutocompleteBase
        data-test='AutocompleteReps'
        placeholder='Search Ops Rep Name'
        ref={ref}
        value={value}
        onChange={changeHandler}
        options={options}
        loading={loading}
        loadingText='Loading...'
        inputIcon='Search'
        renderInputType='withIcon'
        {...props}
      />
    )
  }
)
